import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DeselectOptionActionMeta, SelectOptionActionMeta } from 'react-select';

import type { TargetingCategory, TargetingDimension, TargetingTermValue, TargetingValue } from '../../../models';
import type { ListHelpers } from '../../../utils/listHelpers';
import type { TargetingValueOption } from '../../AdForm/adFormik';
import type { DropdownOption } from '../../Dropdown';
import { ActionTypes } from '../../MultiSelectDropdown';
import { DIMENSION_INCLUDE_EXCLUDE_LIST } from '../constants';
import type { MultiSelectTargetingValue } from '../types';
import {
  formatOptionsToDropdownOptions,
  formatOptionsToTargetingValueOptions,
  getCurrentSelectedValues,
  getIndicesOfUnselectedValuesToRemove,
  getSelectedValues,
  hasValuesChanged,
} from '../utils';
import type { TargetingDropdownOption } from './useTargetingDropdowns';
import { createDropdownOptionFromTargetingValue, optionToTargetingValue } from './utils';

export interface UseTargetingDropdownSelectionProps {
  listHelpers: Pick<ListHelpers<TargetingTermValue>, 'insertAt' | 'replaceAt' | 'removeAt'>;
  targetings: TargetingTermValue[];
  selectedCategory: TargetingDropdownOption | null;
  selectedDimension: TargetingDropdownOption | null;
  selectedValues: TargetingValueOption[];
  clearAll: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  setSelectedValues: (options: TargetingValueOption[]) => void;
}

export interface UseTargetingDropdownSelectionReturn {
  currentSelectedValues: TargetingValueOption[];
  handleInsert: (isInclude: boolean) => void;
  handleSelectValues: (
    options: MultiSelectTargetingValue,
    meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
  ) => void;
  handleClearAll: () => void;
  handleSelectAll: (options: MultiSelectTargetingValue) => void;
  selectedDimensionValues: TargetingValueOption[];
  onClose: () => void;
  onClearFormStateAndSelectedValues: () => void;
}

/**
 * Hook for managing targeting dropdown selection state and actions.
 * Handles inserting, replacing, selecting, and clearing targeting values.
 */
export const useTargetingDropdownSelection = ({
  listHelpers,
  targetings,
  selectedCategory,
  selectedDimension,
  selectedValues,
  clearAll,
  setSelectedValues,
}: UseTargetingDropdownSelectionProps): UseTargetingDropdownSelectionReturn => {
  const [currentSelectedValues, setCurrentSelectedValues] = useState<TargetingValueOption[]>([]);
  const previousSelectedDimension = useRef(selectedDimension);
  // Track the dimension ID for which we've already initialized values to prevent infinite loops
  const initializedForDimensionRef = useRef<string | null>(null);
  const memoizedTargetings = useMemo(() => targetings, [targetings]);
  const filteredTargetings = useMemo(
    () =>
      memoizedTargetings.filter(
        (targeting) => targeting.category !== null && targeting.dimension !== null && targeting.value !== null
      ),
    [memoizedTargetings]
  );
  const onClearFormStateAndSelectedValues = useCallback((): void => {
    setCurrentSelectedValues([]);
    clearAll();
  }, [clearAll]);
  /** Updates include/exclude flag for all items matching the selected dimension */
  const updateIncludeExcludeForDimension = useCallback(
    (
      targetingTerms: TargetingTermValue[],
      category: TargetingCategory,
      dimension: TargetingDimension,
      isInclude: boolean
    ): void => {
      targetingTerms.forEach((term, index) => {
        if (term.dimension.name === selectedDimension?.value) {
          const replacementTerm: TargetingTermValue = {
            category,
            dimension,
            value: { ...term.value },
            include: isInclude,
          };
          listHelpers.replaceAt(index, replacementTerm);
        }
      });
    },
    [listHelpers, selectedDimension?.value]
  );

  function isTargetingValueAlreadyPresent(
    termsToReplace: TargetingTermValue[],
    selectedValue: TargetingValueOption,
    selectedCategory: DropdownOption | null,
    selectedDimension: DropdownOption | null
  ): boolean {
    return !!termsToReplace.find(
      ({ category: { name: categoryName }, dimension: { name: dimensionName }, value: valueMatch }) =>
        categoryName === selectedCategory?.value &&
        dimensionName === selectedDimension?.value &&
        valueMatch.name === selectedValue.value &&
        valueMatch.id === selectedValue.id
    );
  }

  /**
   * Handles inserting new targeting terms into the targeting list.
   *
   * This function performs the following operations:
   * 1. Validates that category, dimension, and selected values are present
   * 2. Converts dropdown options to targeting definition objects (category, dimension)
   * 3. For each selected value, checks if it already exists in the targeting list
   * 4. Inserts new targeting terms at the beginning of the list if they don't already exist
   * 5. For dimensions that support include/exclude flags (not in DIMENSION_INCLUDE_EXCLUDE_LIST),
   *    updates all existing terms matching the selected dimension to use the same include/exclude flag
   * 6. Clears the form state and selected values after insertion
   *
   * @param isInclude - Whether the inserted targeting terms should be included (true) or excluded (false)
   */
  const handleInsert = useCallback(
    (isInclude: boolean): void => {
      // Early return if required selections are missing
      if (selectedCategory && selectedDimension && selectedValues.length) {
        // Convert dropdown options to targeting definition objects
        const category: TargetingCategory = optionToTargetingValue<TargetingCategory>(selectedCategory);
        const dimension: TargetingDimension = optionToTargetingValue<TargetingDimension>(selectedDimension);

        // Create a working copy of filtered targetings to track changes
        const termsToReplace = [...filteredTargetings];

        // Process each selected value and insert if not already present
        currentSelectedValues.forEach((selectedValue: TargetingValueOption) => {
          // Check if this value already exists in the targeting list for the selected category/dimension
          if (!isTargetingValueAlreadyPresent(termsToReplace, selectedValue, selectedCategory, selectedDimension)) {
            // Convert the selected value to a TargetingValue object
            const value: TargetingValue = optionToTargetingValue<TargetingValue>(selectedValue);

            // Create a new targeting term with the category, dimension, value, and include/exclude flag
            const insertTerm: TargetingTermValue = {
              category,
              dimension,
              value,
              include: isInclude,
            };

            // Insert at the beginning of the list (index 0)
            listHelpers.insertAt(0, insertTerm);
            // Also add to our working copy to keep it in sync for subsequent checks
            termsToReplace.unshift(insertTerm);
          }
        });

        // For dimensions that support include/exclude flags, update all existing terms
        // matching this dimension to use the same include/exclude flag
        // (Some dimensions don't support include/exclude, so we skip them)
        if (selectedDimension.id && !DIMENSION_INCLUDE_EXCLUDE_LIST.includes(selectedDimension.id)) {
          updateIncludeExcludeForDimension(termsToReplace, category, dimension, isInclude);
        }
      }

      // Clear the form state and reset selected values after insertion
      onClearFormStateAndSelectedValues();
    },
    [
      selectedCategory,
      selectedDimension,
      selectedValues,
      currentSelectedValues,
      listHelpers,
      filteredTargetings,
      onClearFormStateAndSelectedValues,
      updateIncludeExcludeForDimension,
    ]
  );

  /**
   * Updates the selected values based on select/deselect actions from the dropdown.
   *
   * This function performs the following operations:
   * 1. Maps each action meta (select/deselect) to get the corresponding selected values
   * 2. Flattens the array of arrays into a single array of values
   * 3. Removes duplicate values by filtering to keep only the first occurrence of each unique ID
   * 4. Only updates state if the values have actually changed (prevents unnecessary re-renders)
   *
   * @param metas - Array of select/deselect action metadata from react-select dropdown interactions
   */
  const setValues = useCallback(
    (metas: Array<SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>>): void => {
      // Transform action metas into selected values, then flatten and deduplicate
      const newValues = metas
        // Map each meta to get the selected values based on the action and current state
        .map((meta) => getSelectedValues(selectedValues, meta, selectedDimension))
        // Flatten the array of arrays into a single array
        .flat(1)
        // Remove duplicates by keeping only the first occurrence of each unique ID
        .filter((item, idx, arr) => arr.findIndex((check) => item.id === check.id) === idx);

      // Only update state if values have actually changed to prevent unnecessary re-renders
      if (hasValuesChanged(newValues, selectedValues)) {
        setSelectedValues(newValues);
      }
    },
    [selectedValues, selectedDimension, setSelectedValues]
  );

  const handleSelectValues = useCallback(
    (
      options: MultiSelectTargetingValue,
      meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
    ): void => {
      const valuesToAdd = options.filter((option) => option.value === meta?.option?.value);
      const metas = meta.action === ActionTypes.select ? valuesToAdd.map((option) => ({ ...meta, option })) : [meta];
      setCurrentSelectedValues((current) => getCurrentSelectedValues(current, metas));
      setValues(metas);
    },
    [setCurrentSelectedValues, setValues]
  );

  const handleClearAll = useCallback((): void => {
    setCurrentSelectedValues([]);
    setSelectedValues([]);
  }, [setCurrentSelectedValues, setSelectedValues]);

  const handleSelectAll = useCallback(
    (options: MultiSelectTargetingValue): void => {
      if (selectedDimension) {
        setCurrentSelectedValues(formatOptionsToDropdownOptions(options));
        setSelectedValues(formatOptionsToTargetingValueOptions(options, selectedDimension, selectedValues));
      }
    },
    [selectedDimension, setSelectedValues, selectedValues]
  );

  const selectedDimensionValues = useMemo(() => {
    return selectedValues?.length ? selectedValues.filter(({ dimension }) => dimension === selectedDimension?.id) : [];
  }, [selectedDimension, selectedValues]);

  useEffect(() => {
    if (previousSelectedDimension.current?.id !== selectedDimension?.id) {
      previousSelectedDimension.current = selectedDimension;
      // Reset the initialization tracker when dimension changes
      initializedForDimensionRef.current = null;
      handleClearAll();
    }
  }, [handleClearAll, selectedDimension]);

  useEffect(() => {
    // Only initialize values if:
    // 1. A dimension is selected
    // 2. Values are empty
    // 3. We haven't already initialized for this dimension (prevents infinite loop)
    if (
      selectedDimension?.id &&
      selectedValues.length === 0 &&
      initializedForDimensionRef.current !== selectedDimension.id
    ) {
      // Mark that we've initialized for this dimension before calling setSelectedValues
      initializedForDimensionRef.current = selectedDimension.id;
      setSelectedValues(
        filteredTargetings
          .filter((targetingTableValue) => targetingTableValue.value?.name)
          .map(createDropdownOptionFromTargetingValue)
      );
    }
  }, [selectedDimension, filteredTargetings, selectedValues, setSelectedValues]);

  const onClose = useCallback((): void => {
    const indicesToRemove = getIndicesOfUnselectedValuesToRemove(filteredTargetings, selectedDimension, selectedValues);
    indicesToRemove.forEach((index) => listHelpers.removeAt(index));
  }, [filteredTargetings, selectedDimension, selectedValues, listHelpers]);

  return {
    currentSelectedValues,
    handleInsert,
    handleSelectValues,
    handleClearAll,
    handleSelectAll,
    selectedDimensionValues,
    onClose,
    onClearFormStateAndSelectedValues,
  };
};
